import { Text, SimpleGrid, Box } from '@chakra-ui/react';

import { useAppDispatch } from 'hooks';

import FormInput from 'components/fields/home/FormInput';
import RadioInput from 'components/fields/home/RadioInput';
import SelectInput from 'components/fields/home/SelectInput';
import Divider from 'views/home_form/components/Divider';

import { changeDwellingLiabilityExposures } from 'reducers/homePersonalFormReducer';

import { SECTIONS } from 'views/home_form/constants';
import { FILE_TYPE } from 'variables/constants';

import getRadioYesNo from 'helpers/getRadioYesNo';

import type { APIPersonalHomeApplicationForm } from 'types/api.types';

interface LiabilityExposuresProps {
  dwelling: APIPersonalHomeApplicationForm['dwellings'][0];
  index: number;
}

const LiabilityExposures = ({ dwelling, index }: LiabilityExposuresProps) => {
  const dispatch = useAppDispatch();

  const onFieldChange = (event: any) => {
    const { name, value } = event.target;

    dispatch(
      changeDwellingLiabilityExposures({
        index,
        name,
        value,
      }),
    );
  };

  const onRadioChange = (value: string, field: string) => {
    const booleanValue =
      value === 'true' ? true : value === 'false' ? false : null;

    dispatch(
      changeDwellingLiabilityExposures({
        index,
        name: field,
        value: booleanValue,
      }),
    );
  };

  const onNumberFieldChange = (event: any) => {
    const { name, value } = event.target;
    // Only allow integer numbers (remove any non-digit characters)
    const numericValue = value.replace(/\D/g, '');

    dispatch(
      changeDwellingLiabilityExposures({
        index,
        name,
        value: numericValue,
      }),
    );
  };

  const isRented =
    dwelling.coverages.coverage_form_type?.toLowerCase().includes('rental') ||
    dwelling.coverages.coverage_form_type?.toLowerCase().includes('rented');

  return (
    <>
      <Divider mt="20px" mb="15px">
        Liability Exposures
      </Divider>

      <Text fontSize="sm" mb="15px" color="gray.600">
        All YES answers may require liability extension coverage or remarks
        explaining coverage declined.
      </Text>

      <SimpleGrid columns={6} spacing="20px" mb="20px">
        {/* Question 1 */}
        <RadioInput
          section={SECTIONS.DWELLINGS}
          label="1. Do you own/rent more than one location?"
          subLabel={`Dwelling ${index + 1}`}
          options={[
            { value: 'true', label: 'Yes' },
            { value: 'false', label: 'No' },
          ]}
          value={getRadioYesNo(
            dwelling.liability_exposures.has_own_more_than_one_location,
          )}
          onChange={(value: string) =>
            onRadioChange(value, 'has_own_more_than_one_location')
          }
          message={{
            validationId: 'liability_exposures_526',
            index: index + 1,
            storageKey: 'dwelling',
            manual: {
              message:
                'One or more liability exposures have been disclosed. Please note that a market approval is required with the following details Type of location Location address. Occupancy type. Policy number.',
              show: dwelling.liability_exposures.has_own_more_than_one_location,
            },
            file: FILE_TYPE.TYPE_MARKET_APPROVAL_PROPERTY,
            compact: true,
          }}
          stacked
          required
        />

        {/* Question 2 */}
        <FormInput
          section={SECTIONS.DWELLINGS}
          label="2. Number of weeks location rented to others?"
          subLabel={`Dwelling ${index + 1}`}
          name="number_of_weds_location_rented_to_others"
          value={
            dwelling.liability_exposures
              .number_of_weds_location_rented_to_others
          }
          onChange={onNumberFieldChange}
          message={
            !isRented
              ? {
                  validationId: isRented
                    ? 'liability_exposures_527'
                    : 'liability_exposures_528',
                  index: index + 1,
                  storageKey: 'dwelling',
                  manual: {
                    message:
                      'Please note that market approval is required if the location is the owner-occupied principal residence. To proceed, kindly provide the following details: Details regarding the occupancy; specifically, the rented portion of the dwelling Confirmation of approval from the market, including: Date and time of the call Name of the underwriter you spoke with Details disclosed to the underwriter Confirmation of their approval',
                    show:
                      dwelling.liability_exposures
                        .number_of_weds_location_rented_to_others &&
                      Number(
                        dwelling.liability_exposures
                          .number_of_weds_location_rented_to_others,
                      ) > 0,
                  },
                  file: FILE_TYPE.TYPE_MARKET_APPROVAL_PROPERTY,
                  compact: true,
                }
              : {
                  validationId: '528',
                  index: index + 1,
                  storageKey: 'dwelling',
                  manual: {
                    message:
                      'The location is being rented to others for less than 52 weeks of the year. Please note that market approval is required if the Rented Dwelling is not rented for the entire year, as vacant dwellings are not a permitted. To proceed, kindly provide the following details: Details regarding the occupancy Confirmation of approval from the market, including: Date and time of the call Name of the underwriter you spoke with Details disclosed to the underwriter Confirmation of their approval',
                    show:
                      dwelling.liability_exposures
                        .number_of_weds_location_rented_to_others &&
                      Number(
                        dwelling.liability_exposures
                          .number_of_weds_location_rented_to_others,
                      ) < 52,
                  },
                  file: FILE_TYPE.TYPE_MARKET_APPROVAL_PROPERTY,
                  compact: true,
                }
          }
          required
        />

        {/* Question 3 */}
        <FormInput
          section={SECTIONS.DWELLINGS}
          label="3. Number of rooms rented to others?"
          subLabel={`Dwelling ${index + 1}`}
          name="number_of_rooms_rented_to_others"
          value={dwelling.liability_exposures.number_of_rooms_rented_to_others}
          onChange={onNumberFieldChange}
          message={
            !isRented
              ? {
                  validationId: 'liability_exposures_529',
                  index: index + 1,
                  storageKey: 'dwelling',
                  manual: {
                    message:
                      'The location has 1 or more rooms rented to others. Please note that roomer/boarders are not permitted on a standard property policy, and market approval is required. To proceed, kindly provide the following details: Details regarding the occupancy; specifically, the rented rooms of the dwelling Confirmation of approval from the market, including: Date and time of the call Name of the underwriter you spoke with Details disclosed to the underwriter Confirmation of their approval Please be aware that you cannot proceed until this approval is obtained.',
                    show:
                      dwelling.liability_exposures
                        .number_of_rooms_rented_to_others &&
                      Number(
                        dwelling.liability_exposures
                          .number_of_rooms_rented_to_others,
                      ) > 0,
                  },
                  file: FILE_TYPE.TYPE_MARKET_APPROVAL_PROPERTY,
                  compact: true,
                }
              : undefined
          }
          required
        />

        {/* Question 4 */}
        <FormInput
          section={SECTIONS.DWELLINGS}
          label="4. Daycare operation - number of children"
          subLabel={`Dwelling ${index + 1}`}
          name="daycare_operation_number_of_children"
          value={
            dwelling.liability_exposures.daycare_operation_number_of_children
          }
          onChange={onNumberFieldChange}
          message={{
            validationId: 'liability_exposures_531',
            index: index + 1,
            storageKey: 'dwelling',
            manual: {
              message:
                'Please be aware that markets have specific requirements for daycares, including the number of children and whether the day care is LICENSED or UNLICENSED. Before proceeding, review the market manual to ensure compliance. Provide confirmation below regarding whether the daycare qualifies, specifying the number of children and whether it is licensed or unlicensed. Note that a referral to the market may be required.',
              show:
                dwelling.liability_exposures
                  .daycare_operation_number_of_children &&
                Number(
                  dwelling.liability_exposures
                    .daycare_operation_number_of_children,
                ) > 1,
            },
            compact: true,
          }}
          required
        />

        {/* Question 5 */}
        <RadioInput
          section={SECTIONS.DWELLINGS}
          label="5. Do you own a trampoline?"
          subLabel={`Dwelling ${index + 1}`}
          options={[
            { value: 'true', label: 'Yes' },
            { value: 'false', label: 'No' },
          ]}
          value={getRadioYesNo(dwelling.liability_exposures.has_own_trampoline)}
          onChange={(value: string) =>
            onRadioChange(value, 'has_own_trampoline')
          }
          message={{
            validationId: 'liability_exposures_532',
            index: index + 1,
            storageKey: 'dwelling',
            manual: {
              message:
                'Trampoline is present. Referral may be required; specifically if there is a Home Day Care present at the location. Please carefully review the underwriting manual to confirm eligibility and coverage limits before proceeding.',
              show: dwelling.liability_exposures.has_own_trampoline,
            },
            compact: true,
          }}
          stacked
          required
        />

        {/* Question 6 */}
        <RadioInput
          section={SECTIONS.DWELLINGS}
          label="6. Do you have a garden tractor?"
          subLabel={`Dwelling ${index + 1}`}
          options={[
            { value: 'true', label: 'Yes' },
            { value: 'false', label: 'No' },
          ]}
          value={getRadioYesNo(dwelling.liability_exposures.has_garden_tractor)}
          onChange={(value: string) =>
            onRadioChange(value, 'has_garden_tractor')
          }
          message={{
            validationId: 'liability_exposures_533',
            index: index + 1,
            storageKey: 'dwelling',
            manual: {
              message:
                'Coverage for a garden tractor may be excluded, require scheduling and/or an additional endorsement to extend liability. Please carefully review the underwriting manual to confirm eligibility and coverage limits before proceeding.',
              show: dwelling.liability_exposures.has_garden_tractor,
            },
            compact: true,
          }}
          stacked
          required
        />

        {/* Question 7 */}
        <RadioInput
          section={SECTIONS.DWELLINGS}
          label="7. Do you have a golf cart?"
          subLabel={`Dwelling ${index + 1}`}
          options={[
            { value: 'true', label: 'Yes' },
            { value: 'false', label: 'No' },
          ]}
          value={getRadioYesNo(dwelling.liability_exposures.has_golf_cart)}
          onChange={(value: string) => onRadioChange(value, 'has_golf_cart')}
          message={{
            validationId: 'liability_exposures_534',
            index: index + 1,
            storageKey: 'dwelling',
            manual: {
              message:
                'Coverage for a Golf Cart may be excluded, require scheduling and/or an additional endorsement to extend liability. Please carefully review the underwriting manual to confirm eligibility and coverage limits before proceeding.',
              show: dwelling.liability_exposures.has_golf_cart,
            },
            compact: true,
          }}
          stacked
          required
        />

        {/* Question 8 */}
        <FormInput
          section={SECTIONS.DWELLINGS}
          label="8. Number of saddle/draft animals?"
          subLabel={`Dwelling ${index + 1}`}
          name="number_of_saddle_draft_animals"
          value={dwelling.liability_exposures.number_of_saddle_draft_animals}
          onChange={onNumberFieldChange}
          message={{
            validationId: 'liability_exposures_535',
            index: index + 1,
            storageKey: 'dwelling',
            manual: {
              message:
                'There are 1 or more saddle/draft animals. Market approval is required. To proceed, kindly provide the following details: Details regarding the animal type, which buildings are used to house the animals? Is there a business exposure? Are the animals family pets? Confirmation of approval from the market, including: Date and time of the call Name of the underwriter you spoke with Details disclosed to the underwriter Confirmation of their approval Please be aware that you cannot proceed until this approval is obtained.',
              show:
                dwelling.liability_exposures.number_of_saddle_draft_animals &&
                Number(
                  dwelling.liability_exposures.number_of_saddle_draft_animals,
                ) > 0,
            },
            file: FILE_TYPE.TYPE_MARKET_APPROVAL_PROPERTY,
            compact: true,
          }}
          required
        />

        {/* Question 9 */}
        <RadioInput
          section={SECTIONS.DWELLINGS}
          label="9. Do you own any unlicensed recreational vehicles?"
          subLabel={`Dwelling ${index + 1}`}
          options={[
            { value: 'true', label: 'Yes' },
            { value: 'false', label: 'No' },
          ]}
          value={getRadioYesNo(
            dwelling.liability_exposures
              .has_own_unlicensed_recreational_vehicles,
          )}
          onChange={(value: string) =>
            onRadioChange(value, 'has_own_unlicensed_recreational_vehicles')
          }
          message={{
            validationId: 'liability_exposures_536',
            index: index + 1,
            storageKey: 'dwelling',
            manual: {
              message:
                'Market approval is required. To proceed, kindly provide the following details: Confirmation of approval from the market, including: Date and time of the call Name of the underwriter you spoke with Details disclosed to the underwriter Confirmation of their approval',
              show: dwelling.liability_exposures
                .has_own_unlicensed_recreational_vehicles,
            },
            file: FILE_TYPE.TYPE_MARKET_APPROVAL_PROPERTY,
            compact: true,
          }}
          stacked
          required
        />

        {/* Question 10 */}
        <RadioInput
          section={SECTIONS.DWELLINGS}
          label="10. Renewable energy installation on premises?"
          subLabel={`Dwelling ${index + 1}`}
          options={[
            { value: 'true', label: 'Yes' },
            { value: 'false', label: 'No' },
          ]}
          value={getRadioYesNo(
            dwelling.liability_exposures.has_renewable_energy_installation,
          )}
          onChange={(value: string) =>
            onRadioChange(value, 'has_renewable_energy_installation')
          }
          message={{
            validationId: 'liability_exposures_537',
            index: index + 1,
            storageKey: 'dwelling',
            manual: {
              message:
                'Renewable energy installation installed on premises. Coverage is subject to exclusions or limitations. Market approval is required. To proceed, kindly provide the following details: Confirmation of approval from the market, including: Date and time of the call Name of the underwriter you spoke with Details disclosed to the underwriter Confirmation of their approval Please be aware that you cannot proceed until this approval is obtained.',
              show: dwelling.liability_exposures
                .has_renewable_energy_installation,
            },
            file: FILE_TYPE.TYPE_MARKET_APPROVAL_PROPERTY,
            compact: true,
          }}
          stacked
          required
        />

        {/* Question 11 */}
        <RadioInput
          section={SECTIONS.DWELLINGS}
          label="11. Do you own any watercrafts?"
          subLabel={`Dwelling ${index + 1}`}
          options={[
            { value: 'true', label: 'Yes' },
            { value: 'false', label: 'No' },
          ]}
          value={getRadioYesNo(
            dwelling.liability_exposures.has_own_watercrafts,
          )}
          onChange={(value: string) =>
            onRadioChange(value, 'has_own_watercrafts')
          }
          message={{
            validationId: 'liability_exposures_538',
            index: index + 1,
            storageKey: 'dwelling',
            manual: {
              message:
                'Referral may be required. Coverage for a Watercraft may be excluded, require scheduling and/or an additional endorsement to extend liability. A separate Watercraft policy & application may be required. Please carefully review the underwriting manual to confirm eligibility and coverage limits before proceeding.',
              show: dwelling.liability_exposures.has_own_watercrafts,
            },
            compact: true,
          }}
          stacked
          required
        />

        {/* Question 12 */}
        <FormInput
          section={SECTIONS.DWELLINGS}
          label="12. Number of full-time residence employees?"
          subLabel={`Dwelling ${index + 1}`}
          name="number_of_full_time_residence_employees"
          value={
            dwelling.liability_exposures.number_of_full_time_residence_employees
          }
          onChange={onNumberFieldChange}
          message={{
            validationId: 'liability_exposures_539',
            index: index + 1,
            storageKey: 'dwelling',
            manual: {
              message:
                'Market approval is required. To proceed, kindly provide the following details: Details regarding the employees; Do they live at the location? What is their occupation within the home? Confirmation of approval from the market, including: Date and time of the call Name of the underwriter you spoke with Details disclosed to the underwriter Confirmation of their approval Please be aware that you cannot proceed until this approval is obtained.',
              show:
                dwelling.liability_exposures
                  .number_of_full_time_residence_employees &&
                Number(
                  dwelling.liability_exposures
                    .number_of_full_time_residence_employees,
                ) > 0,
            },
            file: FILE_TYPE.TYPE_MARKET_APPROVAL_PROPERTY,
            compact: true,
          }}
          required
        />

        {/* Question 13 */}
        <RadioInput
          section={SECTIONS.DWELLINGS}
          label="13. Is there a co-occupant that requires coverage?"
          subLabel={`Dwelling ${index + 1}`}
          options={[
            { value: 'true', label: 'Yes' },
            { value: 'false', label: 'No' },
          ]}
          value={getRadioYesNo(
            dwelling.liability_exposures.has_co_occupant_requiring_coverage,
          )}
          onChange={(value: string) =>
            onRadioChange(value, 'has_co_occupant_requiring_coverage')
          }
          message={{
            validationId: 'liability_exposures_540',
            index: index + 1,
            storageKey: 'dwelling',
            manual: {
              show: dwelling.liability_exposures
                .has_co_occupant_requiring_coverage,
              message:
                "One or more liability exposures have been disclosed. Please note that a market approval is required with the following details Co-occupant's name. Date of birth. Relationship to the insured. Reason for not being listed as named insured.",
            },
            file: FILE_TYPE.TYPE_MARKET_APPROVAL_PROPERTY,
            compact: true,
          }}
          stacked
          required
        />

        <FormInput
          section={SECTIONS.DWELLINGS}
          label="Co-occupant name"
          subLabel={`Dwelling ${index + 1}`}
          name="co_occupant_name"
          value={dwelling.liability_exposures.co_occupant_name}
          onChange={onFieldChange}
          required={
            dwelling.liability_exposures.has_co_occupant_requiring_coverage
          }
        />

        {/* Question 14 */}
        <RadioInput
          section={SECTIONS.DWELLINGS}
          label="14. Is there any kind of business operation?"
          subLabel={`Dwelling ${index + 1}`}
          options={[
            { value: 'true', label: 'Yes' },
            { value: 'false', label: 'No' },
          ]}
          value={getRadioYesNo(
            dwelling.liability_exposures.has_any_kind_of_business_operation,
          )}
          onChange={(value: string) =>
            onRadioChange(value, 'has_any_kind_of_business_operation')
          }
          message={{
            validationId: 'liability_exposures_541',
            index: index + 1,
            storageKey: 'dwelling',
            manual: {
              show: dwelling.liability_exposures
                .has_any_kind_of_business_operation,
              message:
                'Market approval is required. To proceed, kindly provide the following details: Details regarding the Business; Classification, Annual Income, Do customers visit the property? Do they have an online sales presence? Confirmation of approval from the market, including: Date and time of the call Name of the underwriter you spoke with Details disclosed to the underwriter Confirmation of their approval Please be aware that you cannot proceed until this approval is obtained.',
            },
            file: FILE_TYPE.TYPE_MARKET_APPROVAL_PROPERTY,
            compact: true,
          }}
          stacked
          required
        />

        <FormInput
          section={SECTIONS.DWELLINGS}
          label="If yes, describe business"
          subLabel={`Dwelling ${index + 1}`}
          name="business_description"
          value={dwelling.liability_exposures.business_description}
          onChange={onFieldChange}
          required={
            dwelling.liability_exposures.has_any_kind_of_business_operation
          }
        />

        {/* Question 15 */}
        <FormInput
          section={SECTIONS.DWELLINGS}
          label="15. Number of dogs in the household?"
          subLabel={`Dwelling ${index + 1}`}
          name="number_of_dogs_in_household"
          value={dwelling.liability_exposures.number_of_dogs_in_household}
          onChange={onNumberFieldChange}
          message={{
            validationId: 'liability_exposures_542',
            index: index + 1,
            storageKey: 'dwelling',
            manual: {
              show:
                (dwelling.liability_exposures.number_of_dogs_in_household ??
                  0) > 0,
              message:
                'Markets may exclude certain dog breeds deemed high-risk. Please confirm the dog breed. In addition, please carefully review the underwriting manual to confirm eligibility and coverage limits before proceeding. Common dog breeds that may be excluded: Pit Bull Rottweilers Doberman Pinschers German Shepherds Chow Chows',
            },
            compact: true,
          }}
          required
        />

        <FormInput
          section={SECTIONS.DWELLINGS}
          label="Breed(s) of dogs"
          subLabel={`Dwelling ${index + 1}`}
          name="breed_of_dogs"
          value={dwelling.liability_exposures.breed_of_dogs}
          onChange={onFieldChange}
          required={
            Number(dwelling.liability_exposures.number_of_dogs_in_household) > 0
          }
        />

        {/* Question 16 */}
        <FormInput
          section={SECTIONS.DWELLINGS}
          label="16. Total property area (if greater than 1 acre)"
          subLabel={`Dwelling ${index + 1}`}
          name="total_property_area"
          value={dwelling.liability_exposures.total_property_area}
          onChange={onNumberFieldChange}
          message={{
            validationId: 'liability_exposures_543',
            index: index + 1,
            storageKey: 'dwelling',
            manual: {
              show: (dwelling.liability_exposures.total_property_area ?? 0) > 1,
              message:
                'Total property area greater than one acre. Market approval is required. To proceed, kindly provide the following details: Details regarding the land; Is there any farming exposure? Any additional structures/outbuildings? Any barns or animals? Confirmation of approval from the market, including: Date and time of the call Name of the underwriter you spoke with Details disclosed to the underwriter Confirmation of their approval',
            },
            file: FILE_TYPE.TYPE_MARKET_APPROVAL_PROPERTY,
            compact: true,
          }}
        />

        <SelectInput
          section={SECTIONS.DWELLINGS}
          label="Unit"
          subLabel={`Dwelling ${index + 1}`}
          name="total_property_area_unit"
          options={[
            { value: '', label: 'Select...' },
            { value: 'acres', label: 'acres' },
            { value: 'hectares', label: 'hectares' },
          ]}
          value={dwelling.liability_exposures.total_property_area_unit}
          onChange={onFieldChange}
          required={dwelling.liability_exposures.total_property_area.length > 0}
        />

        {/* Question 17 */}
        <FormInput
          section={SECTIONS.DWELLINGS}
          label="17. Number of cannabis plants grown on premises?"
          subLabel={`Dwelling ${index + 1}`}
          name="number_of_cannabis_plants_grown"
          value={dwelling.liability_exposures.number_of_cannabis_plants_grown}
          onChange={onNumberFieldChange}
          message={{
            validationId: 'liability_exposures_544',
            index: index + 1,
            storageKey: 'dwelling',
            manual: {
              show:
                (dwelling.liability_exposures.number_of_cannabis_plants_grown ??
                  0) > 4 ||
                ((dwelling.liability_exposures
                  .number_of_cannabis_plants_grown ?? 0) > 0 &&
                  (dwelling.liability_exposures
                    .number_of_cannabis_plants_grown ?? 0) <= 4),
              message:
                (dwelling.liability_exposures.number_of_cannabis_plants_grown ??
                  0) > 4
                  ? 'Cannabis plants grown at the premises exceeds the limit permitted by Ontario law, which allows a maximum of four plants per household.'
                  : 'Please carefully review the underwriting manual to confirm eligibility and coverage limits before proceeding.',
            },
            compact: true,
          }}
          required
        />

        {/* Is the location vacant or do you expect it to be vacant during the policy term? */}
        <RadioInput
          section={SECTIONS.DWELLINGS}
          label="Is the location vacant or do you expect it to be vacant during the policy term?"
          subLabel={`Dwelling ${index + 1}`}
          options={[
            { value: 'true', label: 'Yes' },
            { value: 'false', label: 'No' },
          ]}
          value={getRadioYesNo(
            dwelling.liability_exposures.is_vacant_or_expect_to_be_vacant,
          )}
          onChange={(value: string) =>
            onRadioChange(value, 'is_vacant_or_expect_to_be_vacant')
          }
          stacked
          required
        />

        {/* Question 18 - Other Exposures (larger text area) */}
        <Box gridColumn="span 6">
          <FormInput
            section={SECTIONS.DWELLINGS}
            label="18. Other exposures"
            subLabel={`Dwelling ${index + 1}`}
            name="other_exposures"
            value={dwelling.liability_exposures.other_exposures}
            onChange={onFieldChange}
            message={{
              validationId: 'liability_exposures_546',
              index: index + 1,
              storageKey: 'dwelling',
              manual: {
                show: !!dwelling.liability_exposures.other_exposures,
                message:
                  "You have indicated there are 'Other Exposures' present at the property. Any additional exposures will require market approval prior to binding. Please provide specific information regarding the exposures, and details regarding the market approval",
              },
              file: FILE_TYPE.TYPE_MARKET_APPROVAL_PROPERTY,
              compact: true,
            }}
          />
        </Box>
      </SimpleGrid>
    </>
  );
};

export default LiabilityExposures;
